import { useState } from 'react';

import type { MySnackbarProps } from '@components/snackbar/Snackbar';

import useTranslation from '@hooks/useTranslation';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import {
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    useMediaQuery,
    useTheme,
} from '@mui/material';

export default function Feedback({
    search,
    loading,
    setShowSnackbar,
}: {
    search: string;
    loading: boolean;
    setShowSnackbar: (x: MySnackbarProps) => void;
}) {
    const [feedback, setFeedback] = useState<'good' | 'bad' | ''>('');
    const [searchId, setSearchId] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const { t } = useTranslation('library');

    const resultsGood = t('relevant_results');
    const resultsBad = t('irrelevant_results');

    const skeletonWidth = 230;

    const handleChange = (newFeedback: 'good' | 'bad') => {
        setSubmitting(true);
        setFeedback(newFeedback);
        fetch(`/api/feedback?searchId=${searchId}`, {
            method: 'POST',
            body: JSON.stringify({
                search,
                feedback: newFeedback,
            }),
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((res) => {
                if (res.ok) {
                    return res.json();
                } else {
                    console.error(
                        'error submitting feedback',
                        res.status,
                        res.statusText,
                    );
                    throw new Error(
                        'error submitting semantic search feedback',
                    );
                }
            })
            .then((json) => {
                const { id } = json as { id: string };
                if (id) {
                    // to be used for subsequent updates / changes in feedback
                    setSearchId(id);
                }

                setShowSnackbar({
                    show: true,
                    message: 'شكراً على مساعدتنا في تحسين خدمة البحث',
                    severity: 'success',
                });
            })
            .catch(() =>
                setShowSnackbar({
                    show: true,
                    message:
                        'كان هناك خلل في تثبيت مشاركتك. الرجاء معاودة المحاولة بعد قليل أو التواصل معنا.',
                    severity: 'error',
                }),
            )
            .finally(() => setSubmitting(false));
    };

    if (loading) {
        return (
            <Skeleton
                variant="rectangular"
                width={skeletonWidth}
                height={48}
                sx={{ borderRadius: '5px' }}
            />
        );
    }

    return (
        <ToggleButtonGroup
            color="primary"
            value={feedback}
            disabled={submitting}
            exclusive
            onChange={(_e, value) => handleChange(value)}
            aria-label="semantic feedback"
        >
            <ToggleButton
                value="good"
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'start',
                    gap: 1,
                }}
            >
                <span>{resultsGood}</span>
                <ThumbUpOffAltIcon color="success" />
            </ToggleButton>
            <ToggleButton
                value="bad"
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'start',
                    gap: 1,
                }}
            >
                <span>{resultsBad}</span>
                <ThumbDownOffAltIcon color="error" />
            </ToggleButton>
        </ToggleButtonGroup>
    );
}
